import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Badge, Spinner, Alert, Form } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faCheck, faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import { useSchool } from '../../contexts/SchoolContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { useToast } from '../../contexts/ToastContext';
import SchoolUserService from '../../services/schoolUser.service';
import AttendanceService from '../../services/attendance.service';

const TeacherAttendance = () => {
  const { selectedSchool } = useSchool();
  const { selectedYear } = useAcademicYear();
  const { showToast } = useToast();
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedClassroom, setSelectedClassroom] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [hasExistingAttendance, setHasExistingAttendance] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (selectedSchool && selectedYear) {
      fetchTeacherSubjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool, selectedYear]);

  useEffect(() => {
    if (selectedClassroom && selectedDate) {
      fetchStudentsAndAttendance();
    } else {
      setStudents([]);
      setAttendanceData({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassroom, selectedDate]);

  // Recarregar quando disciplina mudar (para buscar frequências já registradas)
  useEffect(() => {
    if (selectedSubject && students.length > 0) {
      fetchStudentsAndAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject]);

  const fetchTeacherSubjects = async () => {
    try {
      // Backend filtra automaticamente baseado no papel do usuário
      const response = await SchoolUserService.getTeacherAssignments({
        school: selectedSchool,
        year: selectedYear
      });
      setTeacherSubjects(response.results || []);
    } catch (err) {
      console.error('Error fetching teacher subjects:', err);
    }
  };

  const fetchStudentsAndAttendance = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar alunos da turma selecionada
      const studentsResponse = await SchoolUserService.getTeacherStudents({
        school: selectedSchool,
        year: selectedYear,
        classroom: getClassroomFullName()
      });

      const studentsList = studentsResponse.results || [];
      setStudents(studentsList);

      // Inicializar attendance data com presença padrão
      const initialAttendance = {};
      let existingCount = 0;

      // Se disciplina estiver selecionada, buscar frequências já registradas
      if (selectedSubject) {
        const attendanceResponse = await AttendanceService.getAll({
          school: selectedSchool,
          date: selectedDate,
          subject: selectedSubject
        });

        // Criar mapa de frequências por enrollment
        const attendanceMap = {};
        const attendanceList = attendanceResponse.results || attendanceResponse || [];
        attendanceList.forEach(att => {
          // Garantir que a chave seja string
          const enrollmentKey = String(att.enrollment);
          attendanceMap[enrollmentKey] = {
            id: att.id,
            present: att.present
          };
        });

        // Contar registros existentes
        existingCount = attendanceList.length;

        // Inicializar com dados existentes ou padrão
        studentsList.forEach(student => {
          const studentKey = String(student.id);
          if (attendanceMap[studentKey]) {
            initialAttendance[studentKey] = attendanceMap[studentKey];
          } else {
            initialAttendance[studentKey] = {
              id: null,
              present: true // Padrão: presente
            };
          }
        });

        // Atualizar estados de controle
        setHasExistingAttendance(existingCount > 0);
      } else {
        // Sem disciplina selecionada, todos começam como presente
        studentsList.forEach(student => {
          const studentKey = String(student.id);
          initialAttendance[studentKey] = {
            id: null,
            present: true
          };
        });
        setHasExistingAttendance(false);
      }

      setAttendanceData(initialAttendance);
    } catch (err) {
      console.error('Error fetching students and attendance:', err);
      setError('Erro ao carregar alunos e frequência.');
    } finally {
      setLoading(false);
    }
  };

  const getClassroomFullName = () => {
    if (!selectedClassroom) return '';
    const assignment = teacherSubjects.find(
      ts => `${ts.classroom_id}` === selectedClassroom
    );
    return assignment ? `${assignment.classroom_grade} - ${assignment.classroom_education_level} - ${assignment.classroom_name}` : '';
  };

  const handleClassroomChange = (e) => {
    setSelectedClassroom(e.target.value);
    setSelectedSubject('');
  };

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleClearSelection = () => {
    setSelectedClassroom('');
    setSelectedSubject('');
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setStudents([]);
    setAttendanceData({});
  };

  const handleTogglePresence = (studentId) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        present: !prev[studentId]?.present
      }
    }));
  };

  const handleSaveAttendance = async () => {
    try {
      setSaving(true);

      // Preparar dados para envio em lote
      const attendanceRecords = students.map(student => {
        const studentKey = String(student.id);
        const attendance = attendanceData[studentKey];
        return {
          school: selectedSchool,
          enrollment: student.id,
          subject: selectedSubject,
          date: selectedDate,
          present: attendance?.present ?? true
        };
      });

      // Enviar todos os registros em uma única requisição
      const response = await AttendanceService.bulkSave(attendanceRecords);

      // Verificar se houve erros
      if (response.errors && response.errors.length > 0) {
        console.error('Errors saving attendance:', response.errors);
        showToast(
          `Erro ao salvar frequência.`,
          'warning'
        );
      } else {
        showToast(
          `Frequência salva com sucesso!`,
          'success'
        );
      }

      // Recarregar dados
      await fetchStudentsAndAttendance();
    } catch (err) {
      console.error('Error saving attendance:', err);
      showToast('Erro ao salvar frequência.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getUniqueClassrooms = () => {
    const classroomsMap = new Map();
    teacherSubjects.forEach(ts => {
      const key = ts.classroom_id;
      if (!classroomsMap.has(key)) {
        classroomsMap.set(key, {
          id: ts.classroom_id,
          name: `${ts.classroom_grade} - ${ts.classroom_education_level} - ${ts.classroom_name}`
        });
      }
    });
    return Array.from(classroomsMap.values());
  };

  const getSubjectsForClassroom = () => {
    if (!selectedClassroom) return [];
    return teacherSubjects
      .filter(ts => `${ts.classroom_id}` === selectedClassroom)
      .map(ts => ({
        id: ts.subject_id,
        name: ts.subject_name
      }));
  };

  const getAttendanceStats = () => {
    const total = students.length;
    const present = students.filter(s => attendanceData[String(s.id)]?.present).length;
    const absent = total - present;
    const percentage = total > 0 ? ((present / total) * 100).toFixed(1) : 0;

    return { total, present, absent, percentage };
  };

  const stats = getAttendanceStats();
  const canSave = selectedClassroom && selectedSubject && selectedDate && students.length > 0;

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Frequência - {selectedYear}</h4>
          <p className="mb-0">Registre e visualize a frequência dos alunos</p>
        </div>
      </div>

      {/* Estatísticas */}
      <Row className="mb-4">
        <Col md={3}>
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">Total de Alunos</h6>
                  <h3 className="mb-0">{stats.total}</h3>
                </div>
                <div className="text-primary">
                  <FontAwesomeIcon icon={faCalendarCheck} size="2x" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">Presentes</h6>
                  <h3 className="mb-0 text-success">{stats.present}</h3>
                </div>
                <div className="text-success">
                  <FontAwesomeIcon icon={faCheck} size="2x" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">Ausentes</h6>
                  <h3 className="mb-0 text-danger">{stats.absent}</h3>
                </div>
                <div className="text-danger">
                  <FontAwesomeIcon icon={faTimes} size="2x" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between">
                <div>
                  <h6 className="text-muted mb-1">% Presença</h6>
                  <h3 className="mb-0">{stats.percentage}%</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Turma *</Form.Label>
                    <Form.Select
                      value={selectedClassroom}
                      onChange={handleClassroomChange}
                      required
                    >
                      <option value="">Selecione uma turma</option>
                      {getUniqueClassrooms().map((classroom) => (
                        <option key={classroom.id} value={classroom.id}>
                          {classroom.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Disciplina *</Form.Label>
                    <Form.Select
                      value={selectedSubject}
                      onChange={handleSubjectChange}
                      disabled={!selectedClassroom}
                      required
                    >
                      <option value="">Selecione uma disciplina</option>
                      {getSubjectsForClassroom().map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Data *</Form.Label>
                    <Form.Control
                      type="date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      max={today}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    variant="secondary"
                    onClick={handleClearSelection}
                    className="w-100"
                  >
                    Limpar
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Informação sobre registro existente */}
      {selectedClassroom && selectedSubject && selectedDate && students.length > 0 && (
        <Row className="mb-3">
          <Col xs={12}>
            {hasExistingAttendance ? (
              <Alert variant="info" className="mb-0">
                <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                <strong>Modo de Edição:</strong> Já existe frequência registrada para esta data.
                Você pode editar os registros e salvar as alterações.
              </Alert>
            ) : (
              <Alert variant="warning" className="mb-0">
                <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                <strong>Novo Registro:</strong> Não há frequência registrada para esta data.
                Todos os alunos estão marcados como presentes por padrão.
              </Alert>
            )}
          </Col>
        </Row>
      )}

      {/* Lista de Frequência */}
      <Row>
        <Col xs={12}>
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                    {hasExistingAttendance ? 'Editar Frequência' : 'Registrar Frequência'}
                  </h5>
                </Col>
                <Col className="text-end">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleSaveAttendance}
                    disabled={!canSave || saving}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-1" />
                    {saving ? 'Salvando...' : (hasExistingAttendance ? 'Atualizar Frequência' : 'Salvar Frequência')}
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </Spinner>
                </div>
              )}
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}
              {!loading && !error && students.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted">
                    {selectedClassroom && selectedSubject && selectedDate
                      ? 'Nenhum aluno encontrado para esta turma.'
                      : 'Selecione uma turma, disciplina e data para registrar a frequência.'}
                  </p>
                </div>
              )}
              {!loading && !error && students.length > 0 && (
                <Table responsive hover className="align-items-center table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Matrícula</th>
                      <th>Aluno</th>
                      <th>Status</th>
                      <th className="text-center">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => {
                      const studentKey = String(student.id);
                      const isPresent = attendanceData[studentKey]?.present ?? true;
                      return (
                        <tr key={student.id}>
                          <td className="fw-bold">{student.registration}</td>
                          <td>{student.student_name}</td>
                          <td>
                            {isPresent ? (
                              <Badge bg="success">
                                <FontAwesomeIcon icon={faCheck} className="me-1" />
                                Presente
                              </Badge>
                            ) : (
                              <Badge bg="danger">
                                <FontAwesomeIcon icon={faTimes} className="me-1" />
                                Ausente
                              </Badge>
                            )}
                          </td>
                          <td className="text-center">
                            <Button
                              variant={isPresent ? "outline-danger" : "outline-success"}
                              size="sm"
                              onClick={() => handleTogglePresence(studentKey)}
                            >
                              {isPresent ? "Marcar Falta" : "Marcar Presença"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default TeacherAttendance;
